import { keepPreviousData } from '@tanstack/react-query';
import { noddiAsync, URLKeys } from 'noddi-async';
import { useMemo } from 'react';
import type { UserGroupDataQualityFilterInput } from '../../hooks/filters/UserGroup/useUserGroupDataQualityFilters';
import { resolveImpersonationRoleUserGroupIds } from './resolveImpersonationRoleUserGroupIds';

interface Args {
  /** The picked IAM roles; their policies name the user groups they reach. */
  selectedRoleIds: string[];
  /** Data quality params of the user group query below, which resolves to the user group ids the user list takes. */
  dataQualityInput: UserGroupDataQualityFilterInput;
  effectiveServiceOrganizationIds: number[] | undefined;
  isEnabled: boolean;
  serviceDepartmentIds: number[];
}

export const useImpersonationUserGroupScope = ({
  selectedRoleIds,
  dataQualityInput,
  effectiveServiceOrganizationIds,
  isEnabled,
  serviceDepartmentIds
}: Args) => {
  const isDataQualityFilterActive = Object.values(dataQualityInput).some((value) => value != null);

  const {
    data: policies,
    isFetching: isPoliciesFetching,
    error: policiesError
  } = noddiAsync.useGet({
    type: URLKeys.getPaginatedIamPoliciesIgnoringImpersonation,
    input: { pageSize: 500, roleIds: selectedRoleIds },
    queryConfig: {
      placeholderData: keepPreviousData,
      enabled: selectedRoleIds.length > 0
    }
  });
  const roleUserGroupIds = resolveImpersonationRoleUserGroupIds({
    selectedRoleIds,
    policies: policies?.results
  });
  // Placeholder policies belong to the previous role selection. Keep the user query paused until fresh policies load.
  const isWaitingForRolePolicies = selectedRoleIds.length > 0 && isPoliciesFetching;

  // An empty array means the brand resolved to no service organizations: nothing can match,
  // and an empty filter value would widen the query back to every user group.
  const hasEmptyServiceOrganizationScope = effectiveServiceOrganizationIds?.length === 0;
  const isDataQualityQueryEnabled = isDataQualityFilterActive && isEnabled && !hasEmptyServiceOrganizationScope;

  const {
    data: dataQualityUserGroups,
    isPending: isDataQualityUserGroupsPending,
    isFetching: isDataQualityUserGroupsFetching,
    error: dataQualityUserGroupsError
  } = noddiAsync.useGet({
    type: URLKeys.getPaginatedUserGroupsIgnoringImpersonation,
    input: {
      ...dataQualityInput,
      serviceOrganizationIds: effectiveServiceOrganizationIds,
      serviceDepartmentIds: serviceDepartmentIds.length > 0 ? serviceDepartmentIds : undefined
    },
    queryConfig: {
      enabled: isDataQualityQueryEnabled
    }
  });
  const dataQualityUserGroupIds = useMemo(
    () => dataQualityUserGroups?.results.map((userGroup) => userGroup.id),
    [dataQualityUserGroups?.results]
  );
  const userGroupIds = useMemo(
    () =>
      resolveDataQualityUserGroupIds({
        roleUserGroupIds,
        dataQualityUserGroupIds,
        isDataQualityFilterActive
      }),
    [roleUserGroupIds, dataQualityUserGroupIds, isDataQualityFilterActive]
  );
  const isWaitingForDataQualityUserGroups =
    isDataQualityQueryEnabled && (isDataQualityUserGroupsPending || dataQualityUserGroupIds == null);

  return {
    userGroupIds,
    /** The scope resolved to no user group, so the caller must render an empty result without querying. */
    hasNoMatches: userGroupIds?.length === 0,
    isPending: isWaitingForRolePolicies || isWaitingForDataQualityUserGroups,
    isFetching: isPoliciesFetching || isDataQualityUserGroupsFetching,
    error: policiesError ?? dataQualityUserGroupsError
  };
};

const resolveDataQualityUserGroupIds = ({
  roleUserGroupIds,
  dataQualityUserGroupIds,
  isDataQualityFilterActive
}: {
  roleUserGroupIds: number[] | undefined;
  dataQualityUserGroupIds: number[] | undefined;
  isDataQualityFilterActive: boolean;
}): number[] | undefined => {
  if (!isDataQualityFilterActive) {
    return roleUserGroupIds;
  }

  const matchingDataQualityIds = dataQualityUserGroupIds ?? [];
  if (roleUserGroupIds == null) {
    return matchingDataQualityIds;
  }

  const roleUserGroupIdSet = new Set(roleUserGroupIds);
  return matchingDataQualityIds.filter((id) => roleUserGroupIdSet.has(id));
};
